import { useState, useEffect, useContext } from "react";
import { useDispatch } from "react-redux";
import { sortByStatusAndColumn, updateRow } from "@ts/utils";
import { sendRequest } from "@ts/retrieve-data";
import { showToast, toggleButtonLoader } from "@ts/modals";
import { ISubModules, MODULES } from "@ts/constants";
import $ from "jquery";
import * as Sentry from "@sentry/react";
import {
    updateActionModal,
    updateSubmoduleElement,
    updateModuleAction,
    updateIsEdit,
    updateRule,
} from "@features/modalSlice";
import { mainContext } from "@ts/constants";
import { ICellRendererParams, RowNode } from "ag-grid-community";
import { Rule, TRuleActions, TRuleModuleAction } from "@ts/common-types";
import { FaPlay, FaPause, FaTrashAlt, FaEdit } from "react-icons/fa";
import useGetModulesData from "@/hooks/useGetModulesData";
import usePermissionCheck from "@/hooks/usePermissionCheck";


const RowActionButtons: React.FC<ICellRendererParams> = (props) => {
    const api = props.api;
    const { rule_id, type, status } = props.data;
    const ruleId = rule_id;
    const ruleType = type;
    const [ruleStatus, setRuleStatus] = useState<string>();
    const [icon, setIcon] = useState("");
    const dispatch = useDispatch();

    const { setRulesApi } = useContext(mainContext);

    const { disableUpdateAction, disableDeleteAction } = usePermissionCheck({ type: "cellRenderer" });

    useEffect(() => {
        ruleStatus === "active" ? setIcon("play") : setIcon("pause");
        setRuleStatus(status === "active" ? "inactive" : "active");
    }, [ruleStatus, status]);

    function removeRow(id: string, grid: ICellRendererParams) {
        const deletedRow = grid.api.getRowNode(id);
        //get all rows from grid
        const rowData: Rule[] = [];
        grid.api.forEachNode((node: RowNode) => rowData.push(node.data));
        //hide the archived row
        const newRowData = rowData.filter(
            (row) => row.rule_id !== deletedRow.data.rule_id
        );
        grid.api.setRowData(newRowData);
    }

    async function updateRuleStatus(
        id: string,
        ruleToUpdate: string,
        updatedStatus: string
    ) {
        try {
            const response = await sendRequest({
                type: `rules/${ruleToUpdate}/${id}/${updatedStatus}`,
                method: "PATCH",
            });
            if (!response.status) {
                throw new Error(response.message);
            }
            return response;
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
            throw new Error((err as Error).message);
        }
    }

    async function archiveRule(id: string, ruleToArchive: string) {
        try {
            const response = await sendRequest({
                type: `rules/${ruleToArchive}/${id}`,
                method: "delete",
            });

            if (!response.status) {
                throw new Error(response.message);
            }
            return response;
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
            throw new Error((err as Error).message);
        }
    }

    async function toggleActivationButtonClickHandler() {
        try {
            toggleButtonLoader(ruleId, false, "action");
            const response = await updateRuleStatus(
                ruleId,
                ruleType,
                ruleStatus
            );
            if (!response.status) {
                throw new Error(response.message);
            }
            showToast("success", response.message);
            icon === "pause" ? setIcon("play") : setIcon("pause");
            updateRow({
                id: ruleId,
                data: { status: ruleStatus },
                grid: { api: props.api },
            });
            toggleButtonLoader(ruleId, false, "action");
            sortByStatusAndColumn(props.columnApi);
        } catch (err) {
            console.error(err);
            showToast("danger", (err as Error).message);
            toggleButtonLoader(ruleId, false, "action");
            Sentry.captureException(err);
        }
    }

    const editButtonClickHandler = () => {
        toggleButtonLoader(ruleId, true);
        setRulesApi(api);
        dispatch(updateActionModal(true));

        const rule =
            (api.getModel() as Record<string, any>).rowsToDisplay
                .map((r: RowNode) => r.data)
                .find((ruleData: Rule) => ruleData.rule_id === ruleId) || {};
        const path: string = rule.module;
        const availableModules: Array<string> = Object.keys(MODULES);
        let submoduleElement: Array<ISubModules> = [];
        let moduleAction: TRuleModuleAction = [];
        if (!MODULES[path]) {
            for (const modules of availableModules) {
                if (
                    MODULES[modules].hasSubModules &&
                    MODULES[modules].subModules
                ) {
                    moduleAction = MODULES[modules].actions;
                    submoduleElement = MODULES[modules].subModules.filter(
                        (submodule) => submodule.moduleSlug === path
                    );
                }
                if (submoduleElement.length) break;
            }
        } else {
            submoduleElement.push({ module: MODULES[path].moduleSlug });
            (moduleAction as Array<TRuleActions>).push({
                actions: MODULES[path].actions,
            });
        }
        dispatch(updateSubmoduleElement(submoduleElement));
        dispatch(updateModuleAction(moduleAction));
        dispatch(updateIsEdit(true));
        dispatch(updateRule(rule));
    };

    async function archiveRuleButtonHandler() {
        toggleButtonLoader(ruleId, false, "archive");
        try {
            const response = await archiveRule(ruleId, ruleType);
            showToast("success", response.message);
            const newId = $(`div:contains(${ruleId})`);
            const id = newId[10].getAttribute("row-id");
            removeRow(id, props);
            sortByStatusAndColumn(props.columnApi);
        } catch (err) {
            console.error((err as Error).message);
            showToast("danger", (err as Error).message);
            toggleButtonLoader(ruleId, false, "archive");
            Sentry.captureException(err);
        }
        toggleButtonLoader(ruleId, false, "archive");
    }

    const getActionIcon = (btnType: string, actionType: string) => {
        const ActionIcon = btnType === "play"
            ? FaPlay :
            btnType === "update" ?
                FaEdit :
                btnType === 'delete' ?
                    FaTrashAlt :
                    FaPause
        return <ActionIcon style={{ height: '14px' }} className="content"
            id={`${actionType}-btn-${ruleId}`} />
    }

    return (
        <>
            {props.node.rowPinned ? null : (
                <div
                    className="btn-group"
                    style={{ width: "100%", height: "2em" }}
                >
                    <button
                        id={`toggleActivation-rules-btn-${ruleId}`}
                        data-id={ruleId}
                        data-type={ruleType}
                        data-status={ruleStatus}
                        className="btn btn-light"
                        style={{ display: "grid", placeContent: "center" }}
                        onClick={toggleActivationButtonClickHandler}
                        disabled={disableUpdateAction}
                    >
                        {getActionIcon(icon, "action")}
                        <span
                            style={{ display: "none" }}
                            className="loader spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                            id={`action-loader-${ruleId}`}
                        ></span>
                    </button>

                    <button
                        id={`edit-rules-btn-${ruleId}`}
                        data-id={ruleId}
                        data-type={ruleType}
                        data-status={ruleStatus}
                        className="btn btn-light"
                        style={{ display: "grid", placeContent: "center" }}
                        onClick={editButtonClickHandler}
                        disabled={disableUpdateAction}
                    >
                        {
                            getActionIcon("update", "edit")
                        }
                        <span
                            id={`edit-loader-${ruleId}`}
                            style={{ display: "none" }}
                            className="loader spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                        ></span>
                    </button>

                    <button
                        id={`archive-rules-btn-${ruleId}`}
                        data-id={ruleId}
                        data-type={ruleType}
                        data-status={ruleStatus}
                        className="btn btn-light"
                        style={{ display: "grid", placeContent: "center" }}
                        onClick={archiveRuleButtonHandler}
                        disabled={disableDeleteAction}
                    >
                        {
                            getActionIcon("delete", "archive")
                        }
                        <span
                            style={{ display: "none" }}
                            className="loader spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                            id={`archive-loader-${ruleId}`}
                        ></span>
                    </button>
                </div>
            )}
        </>
    );
};

export default RowActionButtons;
